import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm as useReactHookForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { useFormByAccessToken } from '../hooks/useFormByAccessToken';
import { useSubmitForm } from '../hooks/useSubmitForm';
import { FormResponseView } from '../components/FormRenderer/FormResponseView';
import { buildSubmissionSchema, mapSubmissionValues } from '../schemas';

/**
 * Recipient-specific access via `GET /forms/access/{token}` — distinct from
 * `PublicFormSubmissionPage` (`/f/:formId`, requires `audienceType: Public`).
 * Submissions carry `shareToken` so the backend attributes the response to
 * the recipient the link was sent to, regardless of audience type.
 */
export default function FormAccessByTokenPage() {
  const { token } = useParams<{ token: string }>();
  const { form, isLoading, isError, errorCode } = useFormByAccessToken(token ?? '');
  const submitForm = useSubmitForm(form?.id ?? '');
  const [submitted, setSubmitted] = useState(false);

  const sortedFields = form?.fields.slice().sort((a, b) => a.order - b.order) ?? [];
  const schema = buildSubmissionSchema(sortedFields, form?.emailRequired ?? false);

  const rhfForm = useReactHookForm({
    resolver: zodResolver(schema),
    defaultValues: { email: '', fields: {} },
  });

  const handleSubmit = rhfForm.handleSubmit(async (data) => {
    try {
      await submitForm.mutateAsync({
        email: data.email || null,
        isAnonymous: !data.email,
        shareToken: token ?? null,
        values: mapSubmissionValues(data.fields as Record<string, unknown>),
      });
      setSubmitted(true);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  });

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <FormResponseView
        testIdPrefix="forms-access"
        isLoading={isLoading}
        isError={isError}
        errorCode={errorCode}
        form={form}
        submitted={submitted}
        rhfForm={rhfForm}
        onSubmit={handleSubmit}
        isSubmitting={submitForm.isPending}
      />
    </FeatureErrorBoundary>
  );
}
